import { useContext, useEffect, useRef } from 'react';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import updateImageIdByUuidOperation from 'editor/src/store/design/operation/updateImageIdByUuidOperation';
import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import { MediaImage, MediaText, StructureIndex } from 'editor/src/store/design/types';
import { updateImageAction } from 'editor/src/store/gallery/slice';
import { GalleryImage, ImageState } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import {
  buildQrContentFromConfig,
  buildQrPayload,
  DEFAULT_QR_SIZE,
  generateQrImage,
  getQrStyle,
  qrBlobToFile,
} from 'editor/src/lib/qr';
import { getVariableDataColumnValue } from 'editor/src/lib/variableData';
import getCenteredImagePosition from 'editor/src/util/2d/getCenteredImagePosition';

import { UploaderContext } from 'editor/src/component/Uploader/useUploader';

// Give the seller time to finish typing/switching sources before regenerating —
// every regeneration uploads a permanent asset, so the canvas QR intentionally lags
// behind the (instant, upload-free) preview in the configuration dialog. A long
// settle window means one asset per editing burst instead of one per keystroke pause.
const REGENERATE_DEBOUNCE_MS = 2500;

/**
 * Keeps the canvas preview of a QR placeholder in sync with its current field
 * mapping while the seller edits it in the dashboard. Unlike the buyer-side
 * useLiveQrCodes hook, this has no "completeness" gate: the seller must see a real
 * QR (even one encoding blank/placeholder values) instead of a static thumbnail, so
 * they can tell the mapping actually worked.
 */
function useSellerQrImage(element: MediaImage | undefined, address: StructureIndex) {
  const dispatch = useDispatch();
  const uploader = useContext(UploaderContext);
  const lastImageAdded = useSelector((state) => state.gallery.images[0] as GalleryImage | undefined);

  const qrCode = element?.qrCode;
  const spreads = useSelector((state) => state.design.designData?.spreads);
  const variableData = useSelector((state) => state.design.designData?.variableData);
  // `encoded` alone is not proof the QR is on the canvas: the design can carry a stale
  // `encoded` while the element still shows its original placeholder (e.g. a save that
  // kept the config but not the placed image, or an upload that never completed). When
  // the gallery has no record for the element's image (session gallery gap after a
  // reload) we trust `encoded` rather than re-uploading a fresh QR on every open.
  const isQrImagePlaced = useSelector((state) => {
    if (!element) {
      return false;
    }
    const image = state.gallery.images.find((galleryImage) => galleryImage.id === element.imageId);
    return !image || !!image.name?.startsWith('qr-');
  });

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const generationRef = useRef(0);
  const latestPayloadRef = useRef<string | undefined>(undefined);
  const pendingUploadRef = useRef<{ uploadId: string; payload: string } | null>(null);

  const getLinkedText = (elementId: number): string | undefined => {
    let text: string | undefined;
    spreads?.forEach((spread) =>
      spread.pages.forEach((page) =>
        page.groups.media?.forEach((el) => {
          if (el.uuid === elementId && el.type === 'text') {
            text = (el as MediaText).extra?.text;
          }
        }),
      ),
    );
    return text;
  };

  // Column sources render the sample row on the seller's canvas; production swaps in
  // the real row per unit.
  const getColumnValue = (column: string) => getVariableDataColumnValue(variableData, column);

  const payload = qrCode
    ? buildQrPayload(buildQrContentFromConfig(qrCode.type, qrCode.fields, getLinkedText, getColumnValue))
    : undefined;
  latestPayloadRef.current = payload;

  useEffect(() => {
    if (!element || !qrCode || payload === undefined || (payload === qrCode.encoded && isQrImagePlaced)) {
      return undefined;
    }
    const { uuid } = element;

    // Bump the generation synchronously so an already-in-flight upload for an older
    // config is invalidated the moment the seller changes the mapping again.
    generationRef.current += 1;
    const gen = generationRef.current;

    // A cleared `encoded` means the config itself was just (re)applied — an explicit
    // seller action (Done in the configurator, toggle on) that should render right
    // away. The settle delay is only for payload drift while typing in linked fields.
    const delay = qrCode.encoded === undefined ? 0 : REGENERATE_DEBOUNCE_MS;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      generateQrImage({
        content: buildQrContentFromConfig(qrCode.type, qrCode.fields, getLinkedText, getColumnValue),
        style: getQrStyle(qrCode),
        size: DEFAULT_QR_SIZE,
      })
        .then(({ blob }) => {
          // Drop stale results: a newer generation was scheduled, or the resolved
          // payload no longer matches the current config.
          if (generationRef.current !== gen || latestPayloadRef.current !== payload) {
            return;
          }
          const file = qrBlobToFile(blob, `qr-${uuid}`);
          const uploads = uploader.upload([file], { isVisible: false });
          const uploadId = uploads[0]?.id;
          if (uploadId) {
            pendingUploadRef.current = { uploadId, payload };
          }
        })
        .catch((error) => {
          // Keep the previous image; the next config/value change retries.
          // eslint-disable-next-line no-console
          console.warn('[qr] seller QR generation failed', error);
        });
    }, delay);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, qrCode?.encoded, isQrImagePlaced]);

  // Watches the same "newest gallery image" signal PersonalizationSimpleImage already
  // uses for regular uploads (see the isSelectingImage effect above it).
  useEffect(() => {
    const pendingUpload = pendingUploadRef.current;
    // Match on uploaderId: the gallery record keeps the uppy id there while its `id`
    // is swapped to the real asset id once the upload completes.
    const isOurs =
      lastImageAdded &&
      (lastImageAdded.uploaderId === pendingUpload?.uploadId || lastImageAdded.id === pendingUpload?.uploadId);
    if (!pendingUpload || !lastImageAdded || !isOurs) {
      return;
    }
    if (lastImageAdded.state !== ImageState.UPLOADED) {
      return;
    }
    pendingUploadRef.current = null;

    if (!element || !qrCode) {
      return;
    }
    const imagePos = getCenteredImagePosition(element, { width: DEFAULT_QR_SIZE, height: DEFAULT_QR_SIZE });
    batch(() => {
      // No undo step: auto-placement is not a user action, and an undo snapshot here
      // would both pollute the stack and be immediately re-done by this hook.
      dispatch(updateImageIdByUuidOperation(element.uuid, lastImageAdded.id, false));
      dispatch(
        updateMediaElementOperation(
          address,
          { ...imagePos, pr: 0, qrCode: { ...qrCode, encoded: pendingUpload.payload } },
          MediaUpdateActionName.CROPPED,
          true,
        ),
      );
      // Generated QR assets are an implementation detail — keep them out of the
      // Files tab (the gallery list filters hidden images).
      dispatch(updateImageAction({ id: lastImageAdded.id, hidden: true }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastImageAdded?.id, lastImageAdded?.state]);

  useEffect(() => () => clearTimeout(timerRef.current), []);
}

export default useSellerQrImage;
